import { supabase } from "@/integrations/supabase/client";
import { toZonedTime, fromZonedTime } from "date-fns-tz";
import { pb } from "@/lib/pocketbase";

export interface BookingEmailData {
  id: string;
  studentName: string;
  studentEmail: string;
  tutorName: string;
  tutorEmail: string;
  subject: string;
  date: string;
  time: string;
  duration?: string;
  price?: string;
  status: string;
  notes?: string;
  studentPhone?: string;
  tutorPhone?: string;
  tutorTimezone?: string;
  studentTimezone?: string;
  startTime?: string;
  endTime?: string;
  jitsiRoomLink?: string; // Video call room link for the session
  isGroupBooking?: boolean;
  groupSize?: number;
}

export class EmailService {
  // Test email for booking notifications
  private static readonly TEST_EMAIL = "samehmakar87@gmail.com";

  // Fallback admin emails if PocketBase fetch fails
  private static readonly FALLBACK_ADMIN_EMAILS = [
    "Makarsameh87@gmail.com",
    "support@tutornesta.com",
  ];

  /**
   * Fetch admin email list from PocketBase based on notification type
   */
  static async fetchAdminEmails(type: 'kashier' | 'regular'): Promise<string[]> {
    try {
      console.log(`📧 Fetching ${type} admin emails from PocketBase...`);
      
      // Fetch email configuration from PocketBase
      const config = await pb.collection('email_notifications_config').getFirstListItem(`type="${type}"`);
      
      if (config && config.admin_emails && Array.isArray(config.admin_emails)) {
        console.log(`✅ Fetched ${config.admin_emails.length} admin emails for ${type}`);
        return config.admin_emails;
      }
      
      console.warn(`⚠️ No email config found for ${type}, using fallback`);
      return this.FALLBACK_ADMIN_EMAILS;
    } catch (error) {
      console.error(`❌ Error fetching ${type} admin emails from PocketBase:`, error);
      return this.FALLBACK_ADMIN_EMAILS;
    }
  }

  /**
   * Convert time from tutor timezone to student timezone
   */
  private static convertTimeToTimezone(
    time: string,
    date: string,
    fromTimezone: string,
    toTimezone: string
  ): string {
    try {
      let dateString: string;
      if (date.includes("T")) {
        dateString = date.split("T")[0];
      } else if (date.includes(" ")) {
        dateString = date.split(" ")[0];
      } else {
        dateString = date;
      }

      const dateTime = `${dateString}T${time}:00`;
      const fromDate = fromZonedTime(dateTime, fromTimezone);
      const toDate = toZonedTime(fromDate, toTimezone);
      const hours = toDate.getHours().toString().padStart(2, "0");
      const minutes = toDate.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    } catch (error) {
      console.error("Error converting time:", error);
      return time;
    }
  }

  /**
   * Get display times for a recipient with proper timezone handling
   * Returns an object with time string and optional timezone note
   */
  private static getDisplayTimeForRecipient(
    startTime: string,
    endTime: string,
    date: string,
    tutorTimezone: string,
    studentTimezone: string,
    recipientType: "tutor" | "student" | "admin",
  ): { time: string; note: string } {
    const tutorTime = `${startTime} - ${endTime}`;
    const timezonesAreDifferent = tutorTimezone && studentTimezone && tutorTimezone !== studentTimezone;

    // For ADMIN: Show BOTH tutor and student times when timezones differ
    if (recipientType === "admin") {
      if (timezonesAreDifferent) {
        const studentStart = this.convertTimeToTimezone(startTime, date, tutorTimezone, studentTimezone);
        const studentEnd = this.convertTimeToTimezone(endTime, date, tutorTimezone, studentTimezone);
        const studentTime = `${studentStart} - ${studentEnd}`;
        
        return {
          time: tutorTime,
          note: `\n⏰ Tutor Time (${tutorTimezone}): ${tutorTime}\n⏰ Student Time (${studentTimezone}): ${studentTime}`
        };
      }
      return { time: tutorTime, note: tutorTimezone ? `\n⏰ Timezone: ${tutorTimezone}` : "" };
    }

    // For TUTOR: Show tutor's local time
    if (recipientType === "tutor") {
      return { 
        time: tutorTime, 
        note: tutorTimezone ? `\n⏰ Times shown in your timezone: ${tutorTimezone}` : "" 
      };
    }

    // For STUDENT: Convert to student's timezone if different
    if (recipientType === "student") {
      if (timezonesAreDifferent) {
        const studentStart = this.convertTimeToTimezone(startTime, date, tutorTimezone, studentTimezone);
        const studentEnd = this.convertTimeToTimezone(endTime, date, tutorTimezone, studentTimezone);
        const studentTime = `${studentStart} - ${studentEnd}`;
        return { 
          time: studentTime, 
          note: `\n⏰ Times shown in your timezone: ${studentTimezone}` 
        };
      }
      return { time: tutorTime, note: studentTimezone ? `\n⏰ Timezone: ${studentTimezone}` : "" };
    }

    return { time: tutorTime, note: "" };
  }

  /**
   * Send booking approval notification to tutor, student, and admin
   */
  static async sendBookingApprovalNotification(bookingData: BookingEmailData): Promise<{
    success: boolean;
    message: string;
    results?: any[];
    error?: string;
  }> {
    console.log("📧 [EmailService] Sending booking approval notification emails...");
    console.log("📧 [EmailService] Booking data received:", bookingData);

    // Fetch admin emails from PocketBase
    const adminEmails = await this.fetchAdminEmails('regular');

    const recipients = [
      {
        email: bookingData.tutorEmail,
        type: "tutor",
      },
      {
        email: bookingData.studentEmail,
        type: "student",
      },
      ...adminEmails.map((email) => ({
        email,
        type: "admin",
      })),
    ].filter((recipient) => recipient.email); // Filter out recipients without email

    console.log("📧 [EmailService] Recipients for approval notification:", recipients);

    const results = [];
    let hasErrors = false;

    try {
      for (const recipient of recipients) {
        console.log(`📧 [EmailService] Sending approval notification to ${recipient.type}: ${recipient.email}`);

        // Get times with proper timezone handling for each recipient type
        const timeInfo =
          bookingData.startTime && bookingData.endTime && bookingData.tutorTimezone && bookingData.studentTimezone
            ? this.getDisplayTimeForRecipient(
                bookingData.startTime,
                bookingData.endTime,
                bookingData.date,
                bookingData.tutorTimezone,
                bookingData.studentTimezone,
                recipient.type as "tutor" | "student" | "admin",
              )
            : { time: bookingData.time, note: "" };
        
        const displayTime = timeInfo.time;
        const timezoneNote = timeInfo.note;

        // Generate Jitsi room link if booking ID is available
        const jitsiRoomLink = bookingData.jitsiRoomLink || (bookingData.id ? `https://meet.jit.si/tutornesta-${bookingData.id}` : '');

        const emailData = {
          userEmail: recipient.email,
          notificationType: "booking_approved",
          title: `Booking Approved - ${recipient.type.toUpperCase()}`,
          message: `🎉 BOOKING APPROVED!

Your booking has been approved and confirmed!

📅 Session Details:
- Student: ${bookingData.studentName}
- Tutor: ${bookingData.tutorName}
- Subject: ${bookingData.subject}
- Date: ${bookingData.date}
- Time: ${displayTime}${timezoneNote}
- Status: CONFIRMED ✅

${jitsiRoomLink ? `📹 JOIN YOUR VIDEO SESSION:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔗 ${jitsiRoomLink}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚡ Click this link when your session time arrives to join the video call.
💡 Save this link - you'll need it to join your session!
` : ''}

📱 FOR BETTER VIDEO EXPERIENCE:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
للحصول على تجربة فيديو أفضل، ننصحك بتحميل التطبيق خاصة إذا كنت تستخدم هاتفك المحمول.
حمّل التطبيق من الرابط أدناه.

For a better video experience, we recommend downloading the Jitsi Meet app, especially if you're using your phone.

📲 Download Jitsi Meet: https://jitsi.org/downloads/
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${
  recipient.type === "admin"
    ? `📋 Admin Details:
The booking has been approved by the tutor.

📧 Contact Information:
- Student Email: ${bookingData.studentEmail}
- Student Phone: ${bookingData.studentPhone || "Not provided"}
- Tutor Email: ${bookingData.tutorEmail}
- Tutor Phone: ${bookingData.tutorPhone || "Not provided"}`
    : ""
}${
            recipient.type === "tutor"
              ? `👨‍🏫 Tutor: You have successfully approved this booking. The student will be notified.

🔗 Manage your bookings: https://tutornesta.com/lessons`
              : ""
          }${
            recipient.type === "student"
              ? `👨‍🎓 Student: Great news! Your tutor has approved your booking. 

💡 When your session time arrives, click the video session link above to join the call with your tutor!

🔗 View your bookings: https://tutornesta.com/lessons`
              : ""
          }

Sent at: ${new Date().toLocaleString()}

Best regards,
TutorNesta Team`,
          createdAt: new Date().toISOString(),
        };

        const result = await supabase.functions.invoke("send-notification-email", {
          body: emailData,
        });

        if (result.error) {
          console.error(`❌ [EmailService] Email error for ${recipient.type}:`, result.error);
          hasErrors = true;
          results.push(`Failed: ${recipient.type} (${recipient.email})`);
        } else {
          console.log(`✅ [EmailService] Email sent successfully to ${recipient.type}`);
          results.push(`Sent: ${recipient.type} (${recipient.email})`);
        }
      }

      return {
        success: !hasErrors,
        message: hasErrors ? "Some approval emails failed to send" : "All booking approval emails sent successfully",
        results,
      };
    } catch (error) {
      console.error("❌ Booking approval email service error:", error);
      return {
        success: false,
        message: "Email service connection failed",
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  /**
   * Send booking notification to tutor, student, and admin
   * @param bookingData - Booking details
   * @param notificationType - Type of notification: 'kashier' or 'regular'
   */
  static async sendBookingNotification(
    bookingData?: any, 
    notificationType: 'kashier' | 'regular' = 'regular'
  ): Promise<{
    success: boolean;
    message: string;
    results?: any[];
    error?: string;
  }> {
    console.log("📧 [EmailService] === STARTING BOOKING NOTIFICATION EMAILS ===");
    console.log("📧 [EmailService] Notification type:", notificationType);
    console.log("📧 [EmailService] Input booking data:", JSON.stringify(bookingData, null, 2));
    
    // Fetch admin emails from PocketBase based on notification type
    const adminEmails = await this.fetchAdminEmails(notificationType);
    console.log("📧 [EmailService] Admin emails to use:", adminEmails);

    const recipients = [
      {
        email: bookingData?.tutorEmail,
        type: "tutor",
      },
      {
        email: bookingData?.studentEmail,
        type: "student",
      },
      ...adminEmails.map((email) => ({
        email,
        type: "admin",
      })),
    ].filter((recipient) => recipient.email); // Filter out recipients without email

    console.log("📧 [EmailService] Recipients array:", recipients);
    console.log("📧 [EmailService] Total recipients to process:", recipients.length);

    const results = [];
    let hasErrors = false;

    try {
      for (let i = 0; i < recipients.length; i++) {
        const recipient = recipients[i];
        console.log(
          `📧 [EmailService] Processing recipient ${i + 1}/${recipients.length}: ${recipient.type} (${recipient.email})`,
        );

        // Ensure phone numbers are properly handled
        const studentPhone = bookingData?.studentPhone?.toString()?.trim() || "Not provided";
        const tutorPhone = bookingData?.tutorPhone?.toString()?.trim() || "Not provided";

        // Get times with proper timezone handling for each recipient type
        const timeInfo =
          bookingData?.startTime && bookingData?.endTime && bookingData?.tutorTimezone && bookingData?.studentTimezone
            ? this.getDisplayTimeForRecipient(
                bookingData.startTime,
                bookingData.endTime,
                bookingData.date,
                bookingData.tutorTimezone,
                bookingData.studentTimezone,
                recipient.type as "tutor" | "student" | "admin",
              )
            : { time: bookingData?.time || "TBD", note: "" };
        
        const displayTime = timeInfo.time;
        const timezoneNote = timeInfo.note;

        // Generate Jitsi room link if booking ID is available
        const jitsiRoomLink = bookingData?.jitsiRoomLink || (bookingData?.id ? `https://meet.jit.si/tutornesta-${bookingData.id}` : '');
        
        const emailData = {
          userEmail: recipient.email,
          notificationType: "booking_notification",
          title: `New Booking Notification - ${recipient.type.toUpperCase()}`,
          message: `🎓 NEW BOOKING NOTIFICATION

A new booking has been created!

📅 Booking Details:
- Student: ${bookingData?.studentName || "Test Student"}
- Tutor: ${bookingData?.tutorName || "Test Tutor"}
- Subject: ${bookingData?.subject || "General"}
- Date: ${bookingData?.date || new Date().toLocaleDateString()}
- Time: ${displayTime}${timezoneNote}
- Status: ${bookingData?.status || "Pending"}
${bookingData?.isGroupBooking ? `- Group Size: ${bookingData?.groupSize || 2} participants` : ''}

${jitsiRoomLink ? `📹 VIDEO SESSION LINK:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔗 ${jitsiRoomLink}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚡ Click this link when your session time arrives to join the video call.
💡 Save this link - you'll need it to join your session!
` : ''}

📱 FOR BETTER VIDEO EXPERIENCE:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
للحصول على تجربة فيديو أفضل، ننصحك بتحميل التطبيق خاصة إذا كنت تستخدم هاتفك المحمول.
حمّل التطبيق من الرابط أدناه.

For a better video experience, we recommend downloading the Jitsi Meet app, especially if you're using your phone.

📲 Download Jitsi Meet: https://jitsi.org/downloads/
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${
  recipient.type === "admin"
    ? `📋 Admin Action Required: Please review and confirm this booking.

📧 Contact Information:
- Student Email: ${bookingData?.studentEmail || "Not provided"}
- Student Phone: ${studentPhone}
- Tutor Email: ${bookingData?.tutorEmail || "Not provided"}
- Tutor Phone: ${tutorPhone}`
    : ""
}

${
  recipient.type === "tutor"
    ? `👨‍🏫 Tutor: Please confirm your availability for this session.

🔗 To approve/manage bookings, visit: https://tutornesta.com/lessons`
    : ""
}

${recipient.type === "student" ? `👨‍🎓 Student: Your booking request has been submitted. You will receive confirmation from the tutor soon.

💡 TIP: Bookmark the video session link above so you can easily join when your session time arrives!` : ""}

Sent at: ${new Date().toLocaleString()}

Best regards,
TutorNesta Team`,
          createdAt: new Date().toISOString(),
        };

        console.log(`📧 [EmailService] About to send ${recipient.type} email with data:`, {
          userEmail: emailData.userEmail,
          notificationType: emailData.notificationType,
          title: emailData.title,
          messageLength: emailData.message.length,
        });

        const result = await supabase.functions.invoke("send-notification-email", {
          body: emailData,
        });

        console.log(`📧 [EmailService] ${recipient.type} email result:`, result);

        if (result.error) {
          console.error(`❌ [EmailService] Email error for ${recipient.type}:`, result.error);
          hasErrors = true;
          results.push(`Failed: ${recipient.type} (${recipient.email}) - ${result.error.message || "Unknown error"}`);
        } else {
          console.log(`✅ [EmailService] Email sent successfully to ${recipient.type}`);
          results.push(`Sent: ${recipient.type} (${recipient.email})`);
        }
      }

      console.log("📧 [EmailService] === EMAIL SENDING COMPLETE ===");
      console.log("📧 [EmailService] Final results:", results);
      console.log("📧 [EmailService] Has errors:", hasErrors);

      return {
        success: !hasErrors,
        message: hasErrors ? "Some emails failed to send" : "All booking emails sent successfully",
        results,
      };
    } catch (error) {
      console.error("❌ [EmailService] Email service error:", error);
      return {
        success: false,
        message: "Email service connection failed",
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  /**
   * Test email configuration using Supabase Edge Function
   */
  static async testEmailConfiguration(): Promise<{
    success: boolean;
    message: string;
    messageId?: string;
    error?: string;
  }> {
    console.log("🧪 Testing email configuration...");

    try {
      const testEmailData = {
        userEmail: this.TEST_EMAIL,
        notificationType: "test",
        title: "Email Configuration Test",
        message: "This is a test email to verify the email configuration is working correctly.",
        createdAt: new Date().toISOString(),
      };

      const result = await supabase.functions.invoke("send-notification-email", {
        body: testEmailData,
      });

      if (result.error) {
        console.error("❌ Email test failed:", result.error);
        return {
          success: false,
          message: "Email configuration test failed",
          error: result.error.message || "Unknown error",
        };
      }

      console.log("✅ Email test successful:", result);
      return {
        success: true,
        message: "Email configuration test successful",
      };
    } catch (error) {
      console.error("❌ Email test error:", error);
      return {
        success: false,
        message: "Email service connection failed",
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  /**
   * Send payment receipt to tutor, admin, and assistant (if applicable) with PDF and file attachments
   */
  static async sendPaymentReceipt(data: {
    tutorEmail: string;
    tutorName: string;
    amount: number;
    currency: string;
    transactionId: string;
    pdfAttachment: string;
    receiptAttachments?: Array<{ filename: string; content: string; contentType: string }>;
    attachmentUrls?: { name: string; url: string }[];
    assistantEmail?: string; // Add assistant email to recipients if provided
  }): Promise<{
    success: boolean;
    message: string;
    error?: string;
  }> {
    console.log("📧 [EmailService] Sending payment receipt with PDF and attachments...");
    console.log("📧 [EmailService] Attachment URLs:", data.attachmentUrls);
    console.log("📧 [EmailService] Receipt attachments:", data.receiptAttachments?.length || 0);
    console.log("📧 [EmailService] Assistant email:", data.assistantEmail);

    // Build dynamic recipient list
    const recipients: string[] = [data.tutorEmail];

    // Fetch admin emails from PocketBase
    const adminEmails = await this.fetchAdminEmails('regular');
    recipients.push(...adminEmails);

    // Add assistant email if provided
    if (data.assistantEmail && !recipients.includes(data.assistantEmail)) {
      recipients.push(data.assistantEmail);
      console.log("📧 [EmailService] Added assistant email to recipients:", data.assistantEmail);
    }

    console.log("📧 [EmailService] Final recipient list:", recipients);

    let hasErrors = false;

    try {
      for (const email of recipients) {
        // Build HTML attachment links section for email body
        let attachmentLinksHtml = "";
        if (data.attachmentUrls && data.attachmentUrls.length > 0) {
          attachmentLinksHtml = `
<div style="margin: 20px 0; padding: 15px; background-color: #f8f9fa; border-radius: 8px; border-left: 4px solid #667eea;">
  <h3 style="margin: 0 0 15px 0; color: #333; font-size: 16px;">📎 Receipt Attachments</h3>
  ${data.attachmentUrls
    .map(
      (attachment, index) => `
  <div style="margin: 10px 0; padding: 10px; background-color: white; border-radius: 4px;">
    <strong style="color: #667eea;">${index + 1}. ${attachment.name}</strong>
    <br>
    <a href="${attachment.url}" style="color: #667eea; text-decoration: none; font-weight: bold; display: inline-block; margin-top: 5px; padding: 8px 16px; background-color: #667eea; color: white; border-radius: 4px;" target="_blank">⬇️ Download File</a>
  </div>
  `,
    )
    .join("")}
</div>`;
        }

        // Build all attachments array (PDF + receipt files)
        const allAttachments = [
          {
            filename: `payment-receipt-${data.transactionId}.pdf`,
            content: data.pdfAttachment,
            contentType: "application/pdf",
          },
        ];

        // Add receipt file attachments if provided
        if (data.receiptAttachments && data.receiptAttachments.length > 0) {
          allAttachments.push(...data.receiptAttachments);
        }

        // Determine recipient type for personalized message
        const isAssistant = data.assistantEmail && email === data.assistantEmail;
        const isTutor = email === data.tutorEmail;
        const recipientName = isTutor ? data.tutorName : isAssistant ? "Assistant" : "Admin";

        const emailData = {
          userEmail: email,
          notificationType: "payment_receipt",
          title: `🔔 Payment Receipt - ${data.amount} ${data.currency}`,
          message: `Dear ${recipientName},

Please find attached your payment receipt for the recent transaction${isAssistant ? " (processed by you)" : ""}.

💳 Transaction Details:
- Amount: ${data.amount} ${data.currency}
- Transaction ID: ${data.transactionId}
- Date: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}

${attachmentLinksHtml}

📄 Attached Files:
- Payment Receipt PDF (detailed transaction information)
${data.receiptAttachments && data.receiptAttachments.length > 0 ? `- ${data.receiptAttachments.length} Receipt File(s) (original receipt images/documents)` : ""}

${data.attachmentUrls && data.attachmentUrls.length > 0 ? "💡 Tip: You can also download the receipt files from the clickable links above, or find them as attachments to this email." : ""}

If you have any questions about this payment, please contact our support team at support@tutornesta.com.

Best regards,
TutorNesta Team`,
          createdAt: new Date().toISOString(),
          attachments: allAttachments,
        };

        console.log(
          `📧 [EmailService] Sending payment receipt with ${allAttachments.length} total attachment(s) to ${email}${isAssistant ? " (Assistant)" : ""}...`,
        );

        const result = await supabase.functions.invoke("send-notification-email", {
          body: emailData,
        });

        console.log(`📧 [EmailService] Payment receipt result for ${email}:`, result);

        if (result.error) {
          console.error(`❌ [EmailService] Payment receipt error for ${email}:`, result.error);
          hasErrors = true;
        } else {
          console.log(`✅ [EmailService] Payment receipt with PDF and attachment links sent to ${email}`);
        }
      }

      return {
        success: !hasErrors,
        message: hasErrors
          ? "Some emails failed to send"
          : "Payment receipt with PDF and attachments sent successfully",
      };
    } catch (error) {
      console.error("❌ [EmailService] Payment receipt service error:", error);
      return {
        success: false,
        message: "Payment receipt service failed",
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  /**
   * Send notification email with optional attachments
   */
  async sendNotificationEmail(data: {
    userEmail: string;
    userName?: string;
    notificationType: string;
    title: string;
    message: string;
    createdAt: string;
    attachments?: Array<{
      filename: string;
      content: string;
      contentType: string;
    }>;
  }): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    console.log("📧 [EmailService] Sending notification email...");

    try {
      const result = await supabase.functions.invoke("send-notification-email", {
        body: data,
      });

      if (result.error) {
        console.error("❌ [EmailService] Notification email error:", result.error);
        return {
          success: false,
          error: result.error.message || "Unknown error",
        };
      }

      console.log("✅ [EmailService] Notification email sent successfully");
      return {
        success: true,
        message: "Notification email sent successfully",
      };
    } catch (error) {
      console.error("❌ [EmailService] Notification email service error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  /**
   * Format booking data for email notifications
   */
  static formatBookingForEmail(booking: any, studentUser: any, tutorUser: any): BookingEmailData {
    // Format time from startTime and endTime
    const timeDisplay =
      booking.startTime && booking.endTime ? `${booking.startTime} - ${booking.endTime}` : booking.time || "TBD";

    // Extract tutor email from different possible sources
    const tutorEmail =
      tutorUser?.personalEmail || tutorUser?.email || tutorUser?.expand?.user?.email || booking.tutorEmail || "";

    return {
      id: booking.id,
      studentName: studentUser?.name || booking.studentName || "Student",
      studentEmail: studentUser?.email || booking.studentEmail || "",
      tutorName: tutorUser?.name || booking.tutorName || "Tutor",
      tutorEmail: tutorEmail,
      subject: booking.subject || "General Lesson",
      date: booking.date || new Date().toISOString().split("T")[0],
      time: timeDisplay,
      duration: booking.duration || "1 hour",
      price: booking.price ? `$${booking.price}` : "As agreed",
      status: booking.status || "confirmed",
      notes: booking.notes || booking.message || "",
      studentPhone: studentUser?.phone || booking.studentPhone || "Not provided",
      tutorPhone: tutorUser?.phoneNumber || booking.tutorPhone || "Not provided",
      tutorTimezone: tutorUser?.timezone || booking.tutorTimezone || "",
      studentTimezone: studentUser?.timezone || booking.studentTimezone || "",
      startTime: booking.startTime || "",
      endTime: booking.endTime || "",
    };
  }

  /**
   * Send reschedule request notification with approval link
   */
  static async sendRescheduleNotification(data: {
    recipientEmail: string;
    recipientName: string;
    requesterName: string;
    requesterRole: string;
    originalDate: string;
    originalTime: string;
    newDate: string;
    newTime: string;
    subject: string;
    reason: string;
    approvalLink: string;
    recipientTimezone?: string;
  }): Promise<{
    success: boolean;
    message: string;
    error?: string;
  }> {
    console.log("📧 [EmailService] Sending reschedule notification...");

    try {
      const timezoneNote = data.recipientTimezone ? `\n⏰ Times shown in your timezone: ${data.recipientTimezone}` : "";

      const emailData = {
        userEmail: data.recipientEmail,
        notificationType: "reschedule_request",
        title: "🔄 Session Reschedule Request",
        message: `Hello ${data.recipientName},

${data.requesterName} (${data.requesterRole}) has requested to reschedule your upcoming session.

📅 Original Schedule:
- Date: ${data.originalDate}
- Time: ${data.originalTime}${timezoneNote}
- Subject: ${data.subject}

🔄 Proposed New Schedule:
- Date: ${data.newDate}
- Time: ${data.newTime}${timezoneNote}
- Subject: ${data.subject}

💭 Reason: ${data.reason}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📧 Action Required:
Please click the link below to approve or reject this reschedule request:

🔗 ${data.approvalLink}

💡 Tip: If you have any problems with the link, please log in to your TutorNesta account first, then click the link. This ensures the link works properly.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

This link will take you to a page where you can:
✅ Approve - Accept the new date and time
❌ Reject - Decline and keep the original schedule

⏰ Please respond as soon as possible so both parties can plan accordingly.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📝 Alternative Access (if you lose this link):
   1. Log in to your TutorNesta account
   2. Go to "Lessons and Bookings"
   3. Find the "Reschedule Requests" section
   4. View and approve or reject the request there

If you have any questions, please contact us at support@tutornesta.com.

Best regards,
TutorNesta Team`,
        createdAt: new Date().toISOString(),
      };

      const result = await supabase.functions.invoke("send-notification-email", {
        body: emailData,
      });

      if (result.error) {
        console.error("❌ [EmailService] Reschedule notification error:", result.error);
        return {
          success: false,
          message: "Failed to send reschedule notification",
          error: result.error.message || "Unknown error",
        };
      }

      console.log("✅ [EmailService] Reschedule notification sent successfully");
      return {
        success: true,
        message: "Reschedule notification sent successfully",
      };
    } catch (error) {
      console.error("❌ [EmailService] Reschedule notification service error:", error);
      return {
        success: false,
        message: "Email service connection failed",
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  /**
   * Send confirmation to requester that their reschedule request was submitted
   */
  static async sendRescheduleRequestConfirmation(data: {
    recipientEmail: string;
    recipientName: string;
    recipientRole: string;
    approverRole: string;
    originalDate: string;
    originalTime: string;
    newDate: string;
    newTime: string;
    subject: string;
    reason: string;
    recipientTimezone?: string;
  }): Promise<{
    success: boolean;
    message: string;
    error?: string;
  }> {
    console.log("📧 [EmailService] Sending reschedule request confirmation to requester...");

    try {
      const timezoneNote = data.recipientTimezone ? `\n⏰ Times shown in your timezone: ${data.recipientTimezone}` : "";

      const emailData = {
        userEmail: data.recipientEmail,
        notificationType: "reschedule_request_submitted",
        title: "✅ Reschedule Request Submitted",
        message: `Hello ${data.recipientName},

Your reschedule request has been successfully submitted and is waiting for approval from the ${data.approverRole}.

📅 Original Schedule:
- Date: ${data.originalDate}
- Time: ${data.originalTime}${timezoneNote}
- Subject: ${data.subject}

🔄 Requested New Schedule:
- Date: ${data.newDate}
- Time: ${data.newTime}${timezoneNote}
- Subject: ${data.subject}

💭 Your Reason: ${data.reason}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

⏳ Status: PENDING APPROVAL

The ${data.approverRole} has been notified and will review your request. You will receive an email notification once they respond.

You can view your bookings at:
🔗 https://tutornesta.com/lessons

If you have any questions, please contact us at support@tutornesta.com.

Best regards,
TutorNesta Team`,
        createdAt: new Date().toISOString(),
      };

      const result = await supabase.functions.invoke("send-notification-email", {
        body: emailData,
      });

      if (result.error) {
        console.error("❌ [EmailService] Reschedule request confirmation error:", result.error);
        return {
          success: false,
          message: "Failed to send reschedule request confirmation",
          error: result.error.message || "Unknown error",
        };
      }

      console.log("✅ [EmailService] Reschedule request confirmation sent successfully");
      return {
        success: true,
        message: "Reschedule request confirmation sent successfully",
      };
    } catch (error) {
      console.error("❌ [EmailService] Reschedule request confirmation service error:", error);
      return {
        success: false,
        message: "Email service connection failed",
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  /**
   * Send reschedule confirmation (approved or rejected)
   */
  static async sendRescheduleConfirmation(data: {
    recipientEmail: string;
    recipientName: string;
    approverRole: string;
    newDate: string;
    newTime: string;
    subject: string;
    wasApproved: boolean;
    recipientTimezone?: string;
  }): Promise<{
    success: boolean;
    message: string;
    error?: string;
  }> {
    console.log("📧 [EmailService] Sending reschedule confirmation...");

    try {
      const timezoneNote = data.recipientTimezone ? `\n⏰ Times shown in your timezone: ${data.recipientTimezone}` : "";

      const emailData = {
        userEmail: data.recipientEmail,
        notificationType: data.wasApproved ? "reschedule_approved" : "reschedule_rejected",
        title: data.wasApproved ? "✅ Reschedule Request Approved" : "❌ Reschedule Request Rejected",
        message: data.wasApproved
          ? `Hello ${data.recipientName},

Great news! Your reschedule request has been APPROVED by the ${data.approverRole}.

✅ NEW Confirmed Schedule:
- Date: ${data.newDate}
- Time: ${data.newTime}${timezoneNote}
- Subject: ${data.subject}

The booking has been updated with the new date and time. You can view all your bookings at:
🔗 https://tutornesta.com/lessons

Please make sure to join the session at the newly scheduled time.

Best regards,
TutorNesta Team`
          : `Hello ${data.recipientName},

Unfortunately, your reschedule request has been REJECTED by the ${data.approverRole}.

❌ The original booking schedule will remain unchanged.

If you need to reschedule, you can:
- Contact the ${data.approverRole} directly to discuss alternative times
- Submit a new reschedule request with different date/time options
- View your bookings at: https://tutornesta.com/lessons

If you have any questions, please contact us at support@tutornesta.com.

Best regards,
TutorNesta Team`,
        createdAt: new Date().toISOString(),
      };

      const result = await supabase.functions.invoke("send-notification-email", {
        body: emailData,
      });

      if (result.error) {
        console.error("❌ [EmailService] Reschedule confirmation error:", result.error);
        return {
          success: false,
          message: "Failed to send reschedule confirmation",
          error: result.error.message || "Unknown error",
        };
      }

      console.log("✅ [EmailService] Reschedule confirmation sent successfully");
      return {
        success: true,
        message: "Reschedule confirmation sent successfully",
      };
    } catch (error) {
      console.error("❌ [EmailService] Reschedule confirmation service error:", error);
      return {
        success: false,
        message: "Email service connection failed",
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  /**
   * Send group booking invitation email
   */
  static async sendGroupInviteEmail(data: {
    memberEmail: string;
    leaderName: string;
    tutorName: string;
    subject: string;
    date: string;
    time: string;
    inviteUrl: string;
    jitsiRoomLink?: string;
  }): Promise<{
    success: boolean;
    message: string;
    error?: string;
  }> {
    console.log("📧 [EmailService] Sending group booking invite to:", data.memberEmail);

    try {
      const emailData = {
        userEmail: data.memberEmail,
        notificationType: "group_booking_invite",
        title: "You're Invited to a Group Lesson!",
        message: `🎓 GROUP LESSON INVITATION

You've been invited to join a group lesson!

📅 Session Details:
- Leader: ${data.leaderName}
- Tutor: ${data.tutorName}
- Subject: ${data.subject}
- Date: ${data.date}
- Time: ${data.time}

${data.jitsiRoomLink ? `📹 VIDEO SESSION LINK:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔗 ${data.jitsiRoomLink}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚡ Save this link - you'll need it to join your session when the time arrives!
` : ''}

📱 FOR BETTER VIDEO EXPERIENCE:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
للحصول على تجربة فيديو أفضل، ننصحك بتحميل التطبيق خاصة إذا كنت تستخدم هاتفك المحمول.
حمّل التطبيق من الرابط أدناه.

For a better video experience, we recommend downloading the Jitsi Meet app, especially if you're using your phone.

📲 Download Jitsi Meet: https://jitsi.org/downloads/
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

👥 To join this group session, please:
1. Click the link below to create your account
2. Your booking will automatically appear in your dashboard
3. Join the session at the scheduled time using the video link above

🔗 Accept Invitation & Sign Up:
${data.inviteUrl}

This invitation link is unique to you. Please don't share it with others.

Sent at: ${new Date().toLocaleString()}

Best regards,
TutorNesta Team`,
        createdAt: new Date().toISOString(),
      };

      const result = await supabase.functions.invoke("send-notification-email", {
        body: emailData,
      });

      if (result.error) {
        console.error("❌ [EmailService] Group invite email error:", result.error);
        return {
          success: false,
          message: "Failed to send group invite email",
          error: result.error.message || "Unknown error",
        };
      }

      console.log("✅ [EmailService] Group invite email sent successfully");
      return {
        success: true,
        message: "Group invite email sent successfully",
      };
    } catch (error) {
      console.error("❌ [EmailService] Group invite service error:", error);
      return {
        success: false,
        message: "Email service connection failed",
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  /**
   * Send payment reminder email to admins when a tutor submits a claim
   */
  static async sendPaymentReminderToAdmins(data: {
    tutorName: string;
    tutorEmail: string;
    earningId: string;
    bookingId: string;
    amount: number;
    tutorAmount: number;
    subject: string;
    bookingDate: string;
    claimMessage: string;
    studentName: string;
  }): Promise<{ success: boolean; message: string; error?: string }> {
    try {
      console.log("📧 [EmailService] Sending payment reminder to admins...");
      
      // Fetch admin emails using 'regular' type (same as regular bookings)
      const adminEmails = await this.fetchAdminEmails('regular');
      
      if (!adminEmails || adminEmails.length === 0) {
        console.warn("⚠️ [EmailService] No admin emails configured for payment reminders");
        return {
          success: false,
          message: "No admin emails configured",
        };
      }

      console.log("📧 [EmailService] Sending payment reminder to:", adminEmails);

      // Send email to each admin
      const emailPromises = adminEmails.map(async (adminEmail) => {
        const emailData = {
          userEmail: adminEmail,
          notificationType: "payment_reminder",
          title: `💰 Payment Reminder: ${data.tutorName} is requesting payment`,
          message: `📢 PAYMENT REMINDER

A tutor has submitted a payment reminder.

🧑‍🏫 Tutor Details:
- Name: ${data.tutorName}
- Email: ${data.tutorEmail}

📝 Earning Details:
- Earning ID: ${data.earningId}
- Booking ID: ${data.bookingId}
- Subject: ${data.subject}
- Booking Date: ${data.bookingDate}
- Student: ${data.studentName}

💰 Amount Details:
- Original Price: ${data.amount} EGP
- Tutor Amount (80%): ${data.tutorAmount} EGP

💬 Tutor's Message:
"${data.claimMessage}"

⏰ Submitted at: ${new Date().toLocaleString()}

Please review and process this payment at your earliest convenience.

Best regards,
TutorNesta System`,
          createdAt: new Date().toISOString(),
        };

        return supabase.functions.invoke("send-notification-email", {
          body: emailData,
        });
      });

      const results = await Promise.all(emailPromises);
      const successCount = results.filter(r => !r.error).length;

      if (successCount > 0) {
        console.log(`✅ [EmailService] Payment reminder sent to ${successCount}/${adminEmails.length} admins`);
        return {
          success: true,
          message: `Payment reminder sent to ${successCount} admin(s)`,
        };
      } else {
        console.error("❌ [EmailService] Failed to send payment reminder to any admin");
        return {
          success: false,
          message: "Failed to send payment reminder emails",
          error: "All email sends failed",
        };
      }
    } catch (error) {
      console.error("❌ [EmailService] Payment reminder service error:", error);
      return {
        success: false,
        message: "Email service connection failed",
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  /**
   * Send cancellation request notification
   */
  static async sendCancellationNotification(data: {
    recipientEmail: string;
    recipientName: string;
    requesterName: string;
    requesterRole: string;
    date: string;
    time: string;
    subject: string;
    reason: string;
    notificationType: 'request' | 'confirmation' | 'approved' | 'rejected';
    adminNote?: string;
  }): Promise<{
    success: boolean;
    message: string;
    error?: string;
  }> {
    console.log("📧 [EmailService] Sending cancellation notification...", data.notificationType);

    try {
      let title = "";
      let message = "";

      switch (data.notificationType) {
        case 'request':
          title = "❌ Session Cancellation Request";
          message = `Hello ${data.recipientName},

${data.requesterName} (${data.requesterRole}) has requested to cancel the following session:

📚 Session Details:
- Subject: ${data.subject}
- Date: ${data.date}
- Time: ${data.time}

💭 Reason for cancellation:
${data.reason}

⏳ This request is now pending admin review. You will be notified once a decision is made.

If you have any questions, please contact us at support@tutornesta.com.

Best regards,
TutorNesta Team`;
          break;

        case 'confirmation':
          title = "✅ Cancellation Request Submitted";
          message = `Hello ${data.recipientName},

Your cancellation request has been successfully submitted and is pending admin review.

📚 Session Details:
- Subject: ${data.subject}
- Date: ${data.date}
- Time: ${data.time}

💭 Your reason:
${data.reason}

⏳ What happens next:
An admin will review your request and notify you of the decision. The other party has also been notified of your request.

If you have any questions, please contact us at support@tutornesta.com.

Best regards,
TutorNesta Team`;
          break;

        case 'approved':
          title = "✅ Session Cancellation Approved";
          message = `Hello ${data.recipientName},

A cancellation request has been approved. The following session has been cancelled:

📚 Session Details:
- Subject: ${data.subject}
- Date: ${data.date}
- Time: ${data.time}

💭 Original reason:
${data.reason}

The session has been removed from your schedule.

If you have any questions, please contact us at support@tutornesta.com.

Best regards,
TutorNesta Team`;
          break;

        case 'rejected':
          title = "❌ Cancellation Request Rejected";
          message = `Hello ${data.recipientName},

Your cancellation request has been reviewed and rejected. The session will remain on your schedule.

📚 Session Details:
- Subject: ${data.subject}
- Date: ${data.date}
- Time: ${data.time}

💭 Your reason:
${data.reason}

📝 Admin Response:
${data.adminNote || 'No additional notes provided.'}

Please proceed with the session as planned. If you have any concerns, please contact us at support@tutornesta.com.

Best regards,
TutorNesta Team`;
          break;
      }

      const emailData = {
        userEmail: data.recipientEmail,
        notificationType: `cancellation_${data.notificationType}`,
        title,
        message,
        createdAt: new Date().toISOString(),
      };

      const result = await supabase.functions.invoke("send-notification-email", {
        body: emailData,
      });

      if (result.error) {
        console.error("❌ [EmailService] Cancellation notification error:", result.error);
        return {
          success: false,
          message: "Failed to send cancellation notification",
          error: result.error.message || "Unknown error",
        };
      }

      console.log("✅ [EmailService] Cancellation notification sent successfully");
      return {
        success: true,
        message: "Cancellation notification sent successfully",
      };
    } catch (error) {
      console.error("❌ [EmailService] Cancellation notification service error:", error);
      return {
        success: false,
        message: "Email service connection failed",
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }
}
